import { BrowserModule } from "@angular/platform-browser";
import { APP_INITIALIZER, NgModule, isDevMode } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent, LANG } from "./app.component";
import { LoginComponent } from "./auth/login/login.component";
import { ErrorComponent } from "./auth/error/error.component";
import { CallbackComponent } from "./auth/callback/callback.component";
import { ExtensionAuthComponent } from "./auth/extension-auth/extension-auth.component";
import { TenantComponent } from "./auth/tenant/tenant.component";
import { environment } from "../environments/environment";
import { ReactiveFormsModule } from "@angular/forms";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { MainComponent } from "./shared/main/main.component";
import { NgxSonnerToaster } from "ngx-sonner";
import { ApiUrlInterceptor } from "./shared/interceptors/api.url.interceptor";
import { LoadingInterceptor } from "./shared/interceptors/loading.interceptor";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { DragDropDirective } from "./shared/drag-drop.directive";
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { firstValueFrom } from "rxjs";
import { ThemeService } from "./shared/theme/theme.service";
import { WizardChooseComponent } from "./signatures/wizard/wizard-choose/wizard-choose.component";
import { WizardUserComponent } from "./signatures/wizard/wizard-user/wizard-user.component";
import { WizardFinalComponent } from "./signatures/wizard/wizard-final/wizard-final.component";
import { WizardComponent } from "./signatures/wizard/wizard.component";
import { UnitComponent } from "./unit/unit.component";
import { SubmitUserComponent } from "./signatures/submit-user/submit-user.component";
import { WizzardSuccessComponent } from "./signatures/wizard/wizzard-success/wizzard-success.component";
import { WizardUnitComponent } from "./signatures/wizard/wizard-unit/wizard-unit.component";
import { GroupComponent } from "./group/group.component";
import { PlannedComponent } from "./planned/planned.component";
import { DatePipe, DecimalPipe, registerLocaleData } from "@angular/common";
import localePl from "@angular/common/locales/pl";
import localeEs from "@angular/common/locales/es";
import { PlanComponent } from "./plan/plan.component";
import { TranslationsOnlyBlankComponent } from "./shared/blank/translations-only-blank.component";
import { SubscriptionSuccessComponent } from "./plan/subscription-success/subscription-success.component";
import { SubscriptionFailedComponent } from "./plan/subscription-failed/subscription-failed.component";
import { FinanceComponent } from "./finance/finance.component";
import { ClientComponent } from "./client/client.component";
import { ClientDetailComponent } from "./client/client-detail/client-detail.component";
import { ClientDocumentAddDialogComponent } from "./client/client-document-add-dialog/client-document-add-dialog.component";
import { AuthGuard } from "./auth/guard/auth.guard";
import { EmployeesModule } from "./employees/employees.module";
import { StoreModule } from "@ngrx/store";
import { employeesReducer } from "./employees/employees.reducer";
import { EffectsModule } from "@ngrx/effects";
import { EmployeesEffects } from "./employees/employees.effects";
import { SharedModule } from "./shared/shared.module";
import { ConfirmationDialogComponent } from "./shared/confirmation-dialog/confirmation-dialog.component";
import { SettingsModule } from "./settings/settings.module";
import { AddonsModule } from "./addons/addons.module";
import { PlaceholdersComponent } from "./signatures/placeholders/placeholders.component";
import { VariablePaletteComponent } from "./signatures/variable-palette/variable-palette.component";
import { initializeApp, provideFirebaseApp } from "@angular/fire/app";
import {
  getAnalytics,
  provideAnalytics,
  ScreenTrackingService,
  UserTrackingService,
} from "@angular/fire/analytics";
import { getAuth, provideAuth } from "@angular/fire/auth";
import { AccountsModule } from "./accounts/accounts.module";
import { CurrentDomainInterceptor } from "./shared/interceptors/current-domain.interceptor";
import { UserAdminsComponent } from "./user-admins/user-admins.component";
import { UserAdminsEditDialogComponent } from "./user-admins/user-admins-edit-dialog/user-admins-edit-dialog.component";
import { UserAdminsCreateDialogComponent } from "./user-admins/user-admins-create-dialog/user-admins-create-dialog.component";
import { DomainsComponent } from "./domains/domains.component";
import { DomainsCreateDialogComponent } from "./domains/domains-create-dialog/domains-create-dialog.component";
import { DomainsEditDialogComponent } from "./domains/domains-edit-dialog/domains-edit-dialog.component";
import { SignaturesModule } from "./signatures/signatures.module";
import { DigitalCardsModule } from "./digital-cards/digital-cards.module";
import { BackgroundsModule } from "./background/backgrounds.module";
import { MeetBackgroundModule } from "./meet-background/meet-background.module";
import { SignatureEditComponent } from "./signatures/signature-edit/signature-edit.component";
import { SignatureCreateComponent } from "./signatures/signature-create/signature-create.component";
import { AddonsComponent } from "./signatures/addons/addons.component";
import { SignatureDetailsComponent } from "./signatures/signature-details/signature-details.component";
import { InstallBranchComponent } from "./signatures/signature-details/install-branch/install-branch.component";
import { DomainRulesComponent } from "./signatures/signature-details/domain-rules/domain-rules.component";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { ClientEditDialogComponent } from "./client/client-edit-dialog/client-edit-dialog.component";
import { SuperAdminDialogComponent } from "./client/super-admin-dialog/super-admin-dialog.component";
import { StripUndefinedParamsInterceptor } from "./shared/interceptors/strip-undefined-params.interceptor";
import { EmployeeSettingsFormDialogComponent } from "./employee-settings/employee-settings-form-dialog/employee-settings-form-dialog.component";
import { MediaModule } from "./media/media.module";
import { ExpiredTrialComponent } from "./expired-trial/expired-trial.component";
import { ExpiredTrialSuccessComponent } from "./expired-trial/succcess/success.component";
import { ExpiredTrialCancelComponent } from "./expired-trial/cancel/cancel.component";
import { SignatureInstallationStateModule } from "./signature-installation-state/signature-installation-state.module";
import { SignaturesNavigationComponent } from "./signatures/signatures-navigation/signatures-navigation.component";
import { SignaturesPredefinedComponent } from "./signatures/signatures-predefined/signatures-predefined.component";
import { EditSignaturePredefinedComponent } from "./signatures/edit-signature-predefined/edit-signature-predefined.component";
import { DragulaModule } from "ng2-dragula";
import { MatListModule } from "@angular/material/list";
import {
  MatButtonToggle,
  MatButtonToggleGroup,
} from "@angular/material/button-toggle";
import { LiveChatWidgetModule } from "@livechat/widget-angular";
import { ExtensionComponent } from "./extension/extension.component";
import { GeneratorComponent } from "./generator/generator.component";
import { OnboardingComponent } from "./onboarding/onboarding.component";
import { CustomIconAddComponent } from "./onboarding/custom-icon-add/custom-icon-add.component";
import { SocialMediaComponent } from "./shared/social-media/social-media.component";
import { StartComponent } from "./start/start.component";
import { CardDigitalPreviewComponent } from "./digital-cards/card-preview/card-preview.component";
import { DigitalCardsComponent } from "./digital-cards/digital-cards.component";
import { DigitalCardsListComponent } from "./digital-cards/digital-cards-list/digital-cards-list.component";
import { DigitalCardEditorComponent } from "./digital-cards/digital-card-editor/digital-card-editor.component";
import { DigitalCardVariablePaletteDialogComponent } from "./digital-cards/digital-card-variable-palette-dialog/digital-card-variable-palette-dialog.component";
import { DigitalCardStatisticsComponent } from "./digital-cards/digital-card-statistics/digital-card-statistics.component";
import { DigitalCardCustomSourcesComponent } from "./digital-cards/digital-card-custom-sources/digital-card-custom-sources.component";
import { DigitalCardSourceCreateDialogComponent } from "./digital-cards/digital-card-source-create-dialog/digital-card-source-create-dialog.component";
import { DigitalCardSourceShareDialogComponent } from "./digital-cards/digital-card-source-share-dialog/digital-card-source-share-dialog.component";
import { DigitalCardShareDialogComponent } from "./digital-cards/digital-card-share-dialog/digital-card-share-dialog.component";
import { DigitalCardEmployeePickerComponent } from "./digital-cards/digital-card-employee-picker/digital-card-employee-picker.component";
import { DigitalCardEntryDialogComponent } from "./digital-cards/digital-card-entry-dialog/digital-card-entry-dialog.component";
import { BackgroundComponent } from "./background/background.component";
import { BackgroundListComponent } from "./background/background-list/background-list.component";
import { BackgroundCreateComponent } from "./background/background-create/background-create.component";
import { BackgroundEditComponent } from "./background/background-edit/background-edit.component";
import { BackgroundPreviewComponent } from "./background/background-preview/background-preview.component";
import { MeetBackgroundListComponent } from "./meet-background/meet-background-list/meet-background-list.component";
import { MeetBackgroundEditorComponent } from "./meet-background/meet-background-editor/meet-background-editor.component";
import { MeetBackgroundPreviewComponent } from "./meet-background/meet-background-preview/meet-background-preview.component";
import { QRCodeComponent } from "angularx-qrcode";
import { CalculatorsComponent } from "./calculators/calculators.component";
import { WebinarComponent } from "./webinar/webinar.component";
import { EesiComponent } from "./eesi/eesi.component";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import { MatDividerModule } from "@angular/material/divider";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatTooltipModule } from "@angular/material/tooltip";
import { DragDropModule } from "@angular/cdk/drag-drop";
import {
  MatDialogModule,
  MAT_DIALOG_DEFAULT_OPTIONS,
} from "@angular/material/dialog";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatTimepickerModule } from "@angular/material/timepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatTableModule } from "@angular/material/table";
import {
  MatPaginatorIntl,
  MatPaginatorModule,
} from "@angular/material/paginator";
import { TranslatedPaginatorIntl } from "./shared/paginator/translated-paginator-intl";
import { MatChipsModule } from "@angular/material/chips";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatSelectModule } from "@angular/material/select";
import { ProgressBarComponent } from "./shared/progress-bar/progress-bar.component";
import { AddSubdomainDialogComponent } from "./client/add-subdomain-dialog/add-subdomain-dialog.component";
import { VideoDialogComponent } from "./video-dialog/video-dialog.component";
import { SubscriptionComponent } from "./subscription/subscription.component";
import { PublicSubscriptionComponent } from "./public-subscription/public-subscription.component";
import { NoAccessComponent } from "./no-access/no-access.component";
import { ExternalRedirectComponent } from "./external-redirect/external-redirect.component";
import { ResellerComponent } from "./reseller/reseller.component";
import { AddResellerClientDialogComponent } from "./reseller/add-reseller-client-dialog/add-reseller-client-dialog.component";
import { AttachDomainDialogComponent } from "./reseller/attach-domain-dialog/attach-domain-dialog.component";
import { ServiceWorkerModule } from "@angular/service-worker";
import { AuditLogComponent } from "./audit-log/audit-log.component";
import { PrintCardOrdersComponent } from "./print-card-orders/print-card-orders.component";
import { PrintCardOrderDetailComponent } from "./print-card-orders/print-card-order-detail/print-card-order-detail.component";
import { PrintCardOrderItemPreviewDialogComponent } from "./print-card-orders/print-card-order-item-preview-dialog/print-card-order-item-preview-dialog.component";
import { MergePrintCardOrdersDialogComponent } from "./print-card-orders/merge-print-card-orders-dialog/merge-print-card-orders-dialog.component";
import { UpsPackagePlannerComponent } from "./print-card-orders/ups-package-planner/ups-package-planner.component";
import { UpsShippingLabelsDialogComponent } from "./card-orders/ups-shipping-labels-dialog/ups-shipping-labels-dialog.component";
import { UpsShopRatesDialogComponent } from "./print-card-orders/ups-shop-rates-dialog/ups-shop-rates-dialog.component";
import { AttachShipmentDialogComponent } from "./print-card-orders/attach-shipment-dialog/attach-shipment-dialog.component";
import { AuditLogDetailsDialogComponent } from "./audit-log/audit-log-details-dialog/audit-log-details-dialog.component";
import { PrintCardsComponent } from "./print-cards/print-cards.component";
import { PrintCardsListComponent } from "./print-cards/print-cards-list/print-cards-list.component";
import { PrintCardVisualEditorComponent } from "./print-cards/print-card-visual-editor/print-card-visual-editor.component";
import { PrintCardPreviewComponent } from "./print-cards/print-card-preview/print-card-preview.component";
import { PrintCardLayoutThumbComponent } from "./print-cards/print-card-layout-thumb/print-card-layout-thumb.component";
import { PrintCardCanvasComponent } from "./print-cards/print-card-visual-editor/print-card-canvas/print-card-canvas.component";
import { PrintCardElementLayerComponent } from "./print-cards/print-card-visual-editor/print-card-element-layer/print-card-element-layer.component";
import { PrintCardPropertiesPanelComponent } from "./print-cards/print-card-visual-editor/print-card-properties-panel/print-card-properties-panel.component";
import { CardOrdersComponent } from "./card-orders/card-orders.component";
import { CardOrdersListComponent } from "./card-orders/card-orders-list/card-orders-list.component";
import { CardOrderCreateComponent } from "./card-orders/card-order-create/card-order-create.component";
import { CardOrderAccessPointComponent } from "./card-orders/card-order-access-point/card-order-access-point.component";
import { CardOrderDetailComponent } from "./card-orders/card-order-detail/card-order-detail.component";
import { OrderItemsListComponent } from "./card-orders/order-items-list/order-items-list.component";
import { UpsTrackingTimelineComponent } from "./card-orders/ups-tracking-timeline/ups-tracking-timeline.component";
import { UpsPackagesTrackingComponent } from "./card-orders/ups-packages-tracking/ups-packages-tracking.component";
import { MatStepperModule } from "@angular/material/stepper";

export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient);
}

export function translateInitializerFactory(translate: TranslateService) {
  return () => {
    const supportedLangs = ["en", "es", "pl"];
    translate.addLangs(supportedLangs);
    translate.setDefaultLang("en");
    const storedLang = localStorage.getItem(LANG);
    const browserLang = translate.getBrowserLang();
    const lang =
      (storedLang && supportedLangs.includes(storedLang) ? storedLang : null) ||
      (browserLang && supportedLangs.includes(browserLang)
        ? browserLang
        : "en");
    return firstValueFrom(translate.use(lang));
  };
}

export function themeInitializerFactory(theme: ThemeService) {
  return () => {
    void theme;
  };
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ErrorComponent,
    CallbackComponent,
    ExtensionAuthComponent,
    TenantComponent,
    MainComponent,
    DragDropDirective,
    SignatureCreateComponent,
    SignatureDetailsComponent,
    SignatureEditComponent,
    WizardChooseComponent,
    WizardUserComponent,
    WizardUnitComponent,
    WizardFinalComponent,
    WizardComponent,
    UnitComponent,
    SubmitUserComponent,
    WizzardSuccessComponent,
    GroupComponent,
    InstallBranchComponent,
    DomainRulesComponent,
    PlannedComponent,
    PlanComponent,
    TranslationsOnlyBlankComponent,
    SubscriptionSuccessComponent,
    SubscriptionFailedComponent,
    FinanceComponent,
    ClientComponent,
    ClientDetailComponent,
    ClientDocumentAddDialogComponent,
    ConfirmationDialogComponent,
    PlaceholdersComponent,
    VariablePaletteComponent,
    UserAdminsComponent,
    UserAdminsEditDialogComponent,
    UserAdminsCreateDialogComponent,
    DomainsComponent,
    DomainsCreateDialogComponent,
    DomainsEditDialogComponent,
    AddonsComponent,
    ClientEditDialogComponent,
    SuperAdminDialogComponent,
    EmployeeSettingsFormDialogComponent,
    ExpiredTrialComponent,
    ExpiredTrialSuccessComponent,
    ExpiredTrialCancelComponent,
    SignaturesNavigationComponent,
    SignaturesPredefinedComponent,
    EditSignaturePredefinedComponent,
    ExtensionComponent,
    GeneratorComponent,
    ProgressBarComponent,
    AddSubdomainDialogComponent,
    OnboardingComponent,
    CustomIconAddComponent,
    SocialMediaComponent,
    StartComponent,
    CardDigitalPreviewComponent,
    DigitalCardsComponent,
    DigitalCardsListComponent,
    DigitalCardEditorComponent,
    DigitalCardVariablePaletteDialogComponent,
    DigitalCardStatisticsComponent,
    DigitalCardCustomSourcesComponent,
    DigitalCardSourceCreateDialogComponent,
    DigitalCardSourceShareDialogComponent,
    DigitalCardShareDialogComponent,
    DigitalCardEntryDialogComponent,
    DigitalCardEmployeePickerComponent,
    BackgroundComponent,
    BackgroundListComponent,
    BackgroundCreateComponent,
    BackgroundEditComponent,
    BackgroundPreviewComponent,
    MeetBackgroundListComponent,
    MeetBackgroundEditorComponent,
    MeetBackgroundPreviewComponent,
    CalculatorsComponent,
    VideoDialogComponent,
    SubscriptionComponent,
    PublicSubscriptionComponent,
    NoAccessComponent,
    ExternalRedirectComponent,
    WebinarComponent,
    EesiComponent,
    ResellerComponent,
    AddResellerClientDialogComponent,
    AttachDomainDialogComponent,
    AuditLogComponent,
    AuditLogDetailsDialogComponent,
    PrintCardOrdersComponent,
    PrintCardOrderDetailComponent,
    PrintCardOrderItemPreviewDialogComponent,
    MergePrintCardOrdersDialogComponent,
    UpsPackagePlannerComponent,
    UpsShippingLabelsDialogComponent,
    UpsShopRatesDialogComponent,
    AttachShipmentDialogComponent,
    PrintCardsComponent,
    PrintCardsListComponent,
    PrintCardVisualEditorComponent,
    PrintCardPreviewComponent,
    PrintCardLayoutThumbComponent,
    PrintCardCanvasComponent,
    PrintCardElementLayerComponent,
    PrintCardPropertiesPanelComponent,
    CardOrdersComponent,
    CardOrdersListComponent,
    CardOrderCreateComponent,
    CardOrderAccessPointComponent,
    CardOrderDetailComponent,
    OrderItemsListComponent,
    UpsTrackingTimelineComponent,
    UpsPackagesTrackingComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ReactiveFormsModule,
    NgxSonnerToaster,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    TranslateModule,
    EmployeesModule,
    SettingsModule,
    AddonsModule,
    SharedModule,
    AccountsModule,
    SignaturesModule,
    DigitalCardsModule,
    BackgroundsModule,
    MeetBackgroundModule,
    MediaModule,
    StoreModule.forRoot({ employee: employeesReducer }),
    EffectsModule.forRoot([EmployeesEffects]),
    MatProgressSpinnerModule,
    MatProgressBarModule,
    DragulaModule.forRoot(),
    SignatureInstallationStateModule,
    MatListModule,
    MatStepperModule,
    MatButtonToggle,
    MatButtonToggleGroup,
    FormsModule,
    LiveChatWidgetModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatDividerModule,
    MatCheckboxModule,
    MatTooltipModule,
    DragDropModule,
    MatDialogModule,
    MatDatepickerModule,
    MatTimepickerModule,
    MatNativeDateModule,
    MatToolbarModule,
    MatTableModule,
    MatPaginatorModule,
    MatChipsModule,
    MatSlideToggleModule,
    MatSelectModule,
    QRCodeComponent,
    ServiceWorkerModule.register("ngsw-worker.js", {
      enabled: !isDevMode(),
      registrationStrategy: "registerWhenStable:30000",
    }),
  ],
  providers: [
    AuthGuard,
    DatePipe,
    DecimalPipe,
    { provide: MatPaginatorIntl, useClass: TranslatedPaginatorIntl },
    {
      provide: APP_INITIALIZER,
      useFactory: translateInitializerFactory,
      deps: [TranslateService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: themeInitializerFactory,
      deps: [ThemeService],
      multi: true,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        panelClass: "app-dialog-panel",
      },
    },
    { provide: HTTP_INTERCEPTORS, useClass: ApiUrlInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CurrentDomainInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: StripUndefinedParamsInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi()),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => getAuth()),
    provideAnalytics(() => getAnalytics()),
    ScreenTrackingService,
    UserTrackingService,
  ],
})
export class AppModule {
  constructor() {
    registerLocaleData(localeEs, "es");
    registerLocaleData(localePl, "pl");
  }
}
